import React, { useState } from 'react';
import { getAuthHeaders } from '../supabaseClient';
import Icon from './Icon';

const API = 'https://tm-audit-platform-production.up.railway.app';
const APP_VERSION = process.env.REACT_APP_VERSION || null;

// Customer-facing dropdown: friendly label → internal category value. Engineer
// terms are never shown to beta users — note parser_issue is intentionally
// absent here (it stays an internal/admin-only category).
const CATEGORY_OPTIONS = [
    { value: 'false_positive', label: "This doesn't look right" },
    { value: 'missed_opportunity', label: 'I think Audee missed something' },
    { value: 'confusing_report', label: "I don't understand this" },
    { value: 'wrong_dollar_amount', label: 'The dollar amount seems wrong' },
    { value: 'feature_request', label: 'I have an idea' },
    { value: 'other', label: 'Other' },
];
const SEVERITY_OPTIONS = [
    { value: 'critical', label: 'Critical' },
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' },
];

const num = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/[$,]/g, ''));
    return Number.isFinite(n) ? n : null;
};

// Assemble everything we already know about this audit so the user never has to
// retype context. Reads existing finding/parsedData shape — computes nothing new
// about the audit and changes no report output.
function buildContext({ results, fileName, jobName }) {
    const parsedData = results?.parsedData || {};
    const findings = results?.findings || [];
    const active = findings.filter(f => !f.suppressed);

    const inSection = (s) => active.filter(f => f.report_section === s);
    const requires = inSection('Requires Correction');
    const opportunities = inSection('Opportunity');
    const explain = inSection('Prepare to Explain');

    // Recoverable mirrors the report's split: Opportunity + Requires Correction
    // dollars (Prepare-to-Explain items are justify-only, never recoverable).
    const recoverable = [...requires, ...opportunities]
        .reduce((sum, f) => sum + (num(f.dollar_impact) || 0), 0);

    const ruleIds = [...new Set(findings.map(f => f.rule_id).filter(Boolean))];

    return {
        audit_id: results?.auditId || results?.audit_id || null,
        audit_history_id: results?.auditHistoryId || results?.audit_history_id || null,
        document_type: parsedData.documentType || null,
        file_name: fileName || null,
        job_name: jobName || parsedData.jobName || null,
        estimate_total: num(parsedData.invoiceGrandTotal ?? parsedData.grandTotal),
        recoverable_total: recoverable || null,
        requires_correction_count: requires.length,
        opportunity_count: opportunities.length,
        prepare_to_explain_count: explain.length,
        rule_ids: ruleIds,
        finding_ids: ruleIds,
        app_version: APP_VERSION,
        browser_user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    };
}

async function postFeedback(payload) {
    const res = await fetch(API + '/api/feedback', {
        method: 'POST',
        headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload),
    });
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Could not send feedback');
    }
    return res.json();
}

// Compact "Did this audit help?" strip shown directly under the Contractor Mode
// summary / Next Steps. Yes → stores a positive WIN immediately. Something looks
// wrong → opens the issue form. getRuntime() (optional) supplies live interaction
// signals (time on report, PDF downloaded, expanded sections, browser size, …)
// that are merged in at submit time. Never blocks or distracts from the report.
function FeedbackWidget({ results, fileName, jobName, session, getRuntime }) {
    const [mode, setMode] = useState('prompt'); // prompt | form | done
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [doneMessage, setDoneMessage] = useState('');
    const [category, setCategory] = useState('false_positive');
    const [severity, setSeverity] = useState('medium');
    const [notes, setNotes] = useState('');

    const runtime = () => {
        try { return (typeof getRuntime === 'function' ? getRuntime() : {}) || {}; }
        catch (e) { return {}; }
    };

    const sendPositive = async () => {
        setSubmitting(true);
        setError(null);
        try {
            await postFeedback({ feedback_type: 'positive', ...buildContext({ results, fileName, jobName }), ...runtime() });
            setDoneMessage('Thanks — this helps us improve Audee.');
            setMode('done');
        } catch (e) {
            setError(e.message);
        }
        setSubmitting(false);
    };

    const sendIssue = async () => {
        setSubmitting(true);
        setError(null);
        try {
            await postFeedback({
                feedback_type: 'issue',
                category,
                severity,
                notes,
                ...buildContext({ results, fileName, jobName }),
                ...runtime(),
            });
            setDoneMessage("Thanks — we'll review this.");
            setMode('done');
        } catch (e) {
            setError(e.message);
        }
        setSubmitting(false);
    };

    if (mode === 'done') {
        return (
            <div className="feedback-widget feedback-widget-strip feedback-widget-done">
                <span className="feedback-done-check"><Icon name="check-circle" size={16} /></span>
                <span className="feedback-done-text">{doneMessage}</span>
            </div>
        );
    }

    return (
        <div className="feedback-widget feedback-widget-strip">
            {mode === 'prompt' && (
                <div className="feedback-prompt-row">
                    <span className="feedback-prompt-label">Did this audit help?</span>
                    <div className="feedback-prompt-actions">
                        <button className="feedback-btn feedback-btn-helpful" onClick={sendPositive} disabled={submitting}>
                            {submitting ? 'Sending…' : 'Yes'}
                        </button>
                        <button className="feedback-btn feedback-btn-issue" onClick={() => { setError(null); setMode('form'); }} disabled={submitting}>
                            Something looks wrong
                        </button>
                    </div>
                </div>
            )}

            {mode === 'form' && (
                <div className="feedback-form">
                    <div className="feedback-form-title">Tell us what looks wrong</div>

                    <label className="feedback-field">
                        <span className="feedback-field-label">What's the issue?</span>
                        <select className="feedback-select" value={category} onChange={e => setCategory(e.target.value)} disabled={submitting}>
                            {CATEGORY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </label>

                    <label className="feedback-field">
                        <span className="feedback-field-label">How urgent is it?</span>
                        <select className="feedback-select" value={severity} onChange={e => setSeverity(e.target.value)} disabled={submitting}>
                            {SEVERITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </label>

                    <label className="feedback-field">
                        <span className="feedback-field-label">Notes</span>
                        <textarea
                            className="feedback-textarea"
                            rows={4}
                            placeholder="What happened? What did you expect?"
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                            disabled={submitting}
                        />
                    </label>

                    {error && <div className="feedback-error">{error}</div>}

                    <div className="feedback-form-actions">
                        <button className="feedback-btn feedback-btn-cancel" onClick={() => setMode('prompt')} disabled={submitting}>
                            Cancel
                        </button>
                        <button className="feedback-btn feedback-btn-send" onClick={sendIssue} disabled={submitting}>
                            {submitting ? 'Sending…' : 'Send feedback'}
                        </button>
                    </div>
                </div>
            )}

            {mode === 'prompt' && error && <div className="feedback-error">{error}</div>}
        </div>
    );
}

export default FeedbackWidget;
